import React from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Logout as LogoutIcon, EditOutlined } from '@mui/icons-material';
import mainLogo from '../../images/main-logo.avif';

const drawerWidth = 280;

const Sidebar = ({ open, onClose, menuItems, title, user, isMobile, onLogout }) => {
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U';
  const roleLabel = user?.role ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}` : 'User';
  const editProfileIndex = menuItems.findIndex((item) => item.label?.toLowerCase() === 'edit profile');

  const handleMenuClick = (index) => {
    // Handle menu item click
    if (menuItems[index].onClick) {
      menuItems[index].onClick();
    }
    // Close sidebar on mobile after selection
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogoutClick = async () => {
    if (onLogout) {
      await onLogout();
    }
  };

  const drawerVariant = isMobile ? 'temporary' : 'persistent';

  return (
    <Drawer
      variant={drawerVariant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: isMobile ? 'block' : 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#2c3e50',
          boxShadow: isMobile ? 'none' : '2px 0 8px rgba(0,0,0,0.1)',
          color: 'white',
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header section for mobile */}
      {isMobile && (
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: '#2c3e50',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={mainLogo}
              alt="Analytikal DNA Logo"
              sx={{
                height: 40,
                width: 'auto',
                maxWidth: 150,
                objectFit: 'contain',
              }}
            />
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Desktop header */}
      {!isMobile && (
        <>
          <Box sx={{ 
            p: { xs: 2, sm: 2.5 },
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
          }}>
            <Box
              component="img"
              src={mainLogo}
              alt="Analytikal DNA Logo"
              sx={{
                height: 45,
                width: 'auto',
                maxWidth: 160,
                objectFit: 'contain',
              }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 0.5,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.8,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                AnalytikalDNA
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
        </>
      )}

      {/* Menu items - scrollable area */}
      <List sx={{ 
        mt: 1,
        px: { xs: 0.5, sm: 1 },
        flexGrow: 1,
        overflow: 'auto',
      }}>
        {menuItems.map((item, index) => {
          const isActive = item.active !== undefined ? item.active : false;
          
          // Handle external links
          if (item.isExternal) {
            return (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mx: { xs: 0.5, sm: 1 },
                    mb: 0.5,
                    borderRadius: 2,
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                    py: { xs: 1, sm: 1.5 },
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: 'white', 
                      minWidth: { xs: 36, sm: 40 },
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label}
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontWeight: 'normal',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      } 
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          }
          
          // Handle internal navigation
          return (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => handleMenuClick(index)}
                sx={{
                  mx: { xs: 0.5, sm: 1 },
                  mb: 0.5,
                  borderRadius: 2,
                  backgroundColor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                  py: { xs: 1, sm: 1.5 },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: 'white', 
                    minWidth: { xs: 36, sm: 40 },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      fontWeight: isActive ? 'bold' : 'normal',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    } 
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Profile info and logout at bottom */}
      {onLogout && (
        <>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', my: 1 }} />
          <Box
            sx={{
              mx: { xs: 1, sm: 1.5 },
              mb: 1,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.14)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
              <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40, fontWeight: 700 }}>{initials}</Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {user?.firstName || ''} {user?.lastName || ''}
                </Typography>
                <Chip
                  label={roleLabel}
                  size="small"
                  sx={{
                    mt: 0.5,
                    height: 20,
                    fontWeight: 600,
                    backgroundColor: 'rgba(25, 118, 210, 0.3)',
                    color: '#e3f2fd',
                  }}
                />
              </Box>
              {editProfileIndex !== -1 && (
                <IconButton
                  size="small"
                  onClick={() => handleMenuClick(editProfileIndex)}
                  sx={{
                    color: '#e3f2fd',
                    backgroundColor: 'rgba(25, 118, 210, 0.25)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.4)',
                    },
                  }}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.95, lineHeight: 1.35, wordBreak: 'break-word' }}>
              {user?.email || ''}
            </Typography>
            {user?.phone && (
              <Typography variant="body2" sx={{ opacity: 0.95, lineHeight: 1.35 }}>
                {user.phone}
              </Typography>
            )}
          </Box>
          <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogoutClick}
                sx={{
                  mx: { xs: 0.5, sm: 1 },
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  },
                  py: { xs: 1, sm: 1.5 },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: '#ff6b6b', 
                    minWidth: { xs: 36, sm: 40 },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout"
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      fontWeight: 'normal',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      color: '#ff6b6b'
                    } 
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default Sidebar;

